import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { MutationError, SubmitButton, SuccessPanel } from '../../../components/form-feedback';
import { NumberPad } from '../../../components/number-pad';
import { RadioPillGroup } from '../../../components/radio-pill-group';
import { api } from '../../../lib/api';
import { alertsQuery, lastFeedingQuery, summaryQuery } from '../../../lib/queries';
import {
  type LogFeedingFormData,
  LogFeedingFormSchema,
  type LogFeedingResponse,
} from '../../../lib/schemas';

export const Route = createFileRoute('/_auth/log/feeding')({
  component: LogFeedingPage,
});

function LogFeedingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successData, setSuccessData] = useState<LogFeedingResponse | null>(null);
  const [endWeightStr, setEndWeightStr] = useState('');

  const now = new Date();
  const defaultDate = now.toISOString().slice(0, 10);
  const defaultTime = now.toTimeString().slice(0, 5);

  // Pre-fill from last feeding
  const { data: lastFeeding } = useQuery(lastFeedingQuery);

  const lastFeedingData = lastFeeding?.feedings[0];

  const form = useForm<LogFeedingFormData>({
    resolver: zodResolver(LogFeedingFormSchema),
    defaultValues: {
      date: defaultDate,
      time_delivered: defaultTime,
      group: (lastFeedingData?.group as LogFeedingFormData['group']) ?? 'milking',
      ...(lastFeedingData?.head_count != null && { head_count: lastFeedingData.head_count }),
      batch_hours: 24,
      notes: '',
    },
  });

  const headCount = form.watch('head_count');
  const lbsPerHeadPreview =
    endWeightStr && headCount ? Math.round((Number(endWeightStr) / headCount) * 10) / 10 : null;

  const mutation = useMutation({
    mutationFn: (data: LogFeedingFormData) => {
      const payload = {
        ...data,
        end_weight_lbs: endWeightStr ? Number(endWeightStr) : undefined,
        notes: data.notes || undefined,
        bunk_status: data.bunk_status || undefined,
      };
      return api.post<LogFeedingResponse>('/api/feeding', payload);
    },
    onSuccess: async (data) => {
      setSuccessData(data);
      await queryClient.invalidateQueries({ queryKey: summaryQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: alertsQuery.queryKey });
    },
  });

  const onSubmit = form.handleSubmit((data) => {
    mutation.mutate(data);
  });

  // --- Success state ---
  if (successData) {
    return (
      <SuccessPanel
        title="Feeding Logged"
        onLogAnother={() => {
          setSuccessData(null);
          setEndWeightStr('');
          form.reset({
            date: new Date().toISOString().slice(0, 10),
            time_delivered: new Date().toTimeString().slice(0, 5),
            group: 'milking',
            batch_hours: 24,
            notes: '',
          });
        }}
        onHome={() => navigate({ to: '/' })}
      >
        <p className="text-ndd-300">ID: {successData.feeding_id}</p>
        {successData.computed.lbs_per_head != null && (
          <p className="mt-2 text-3xl font-bold">{successData.computed.lbs_per_head} lbs/head</p>
        )}
        {successData.computed.vs_7day_avg != null && (
          <p className="mt-1 text-ndd-300">
            {successData.computed.vs_7day_avg > 0 ? '+' : ''}
            {successData.computed.vs_7day_avg} vs 7-day avg
          </p>
        )}
      </SuccessPanel>
    );
  }

  // --- Form state ---
  return (
    <div className="mx-auto max-w-lg px-4 py-4">
      <h1 className="mb-4 text-2xl font-bold">Log Feeding</h1>

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Date */}
        <div>
          <label htmlFor="date" className="mb-1 block text-sm text-ndd-300">
            Date
          </label>
          <input
            id="date"
            type="date"
            {...form.register('date')}
            className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white"
          />
          {form.formState.errors.date && (
            <p className="mt-1 text-sm text-red-400">{form.formState.errors.date.message}</p>
          )}
        </div>

        {/* Time */}
        <div>
          <label htmlFor="time" className="mb-1 block text-sm text-ndd-300">
            Time
          </label>
          <input
            id="time"
            type="time"
            {...form.register('time_delivered')}
            className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white"
          />
        </div>

        {/* Group */}
        <div>
          <label htmlFor="group" className="mb-1 block text-sm text-ndd-300">
            Group
          </label>
          <select
            id="group"
            {...form.register('group')}
            className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white"
          >
            <option value="milking">Milking</option>
            <option value="dry">Dry</option>
            <option value="heifers">Heifers</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Head Count */}
        <div>
          <label htmlFor="head_count" className="mb-1 block text-sm text-ndd-300">
            Head Count *
          </label>
          <input
            id="head_count"
            type="number"
            inputMode="numeric"
            placeholder="125"
            {...form.register('head_count')}
            className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-lg text-white"
          />
          {form.formState.errors.head_count && (
            <p className="mt-1 text-sm text-red-400">{form.formState.errors.head_count.message}</p>
          )}
        </div>

        {/* Batch Hours */}
        <div>
          <label htmlFor="batch_hours" className="mb-1 block text-sm text-ndd-300">
            Batch Hours
          </label>
          <input
            id="batch_hours"
            type="number"
            inputMode="numeric"
            {...form.register('batch_hours')}
            className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white"
          />
        </div>

        {/* End Weight — NumberPad */}
        <div>
          <p className="mb-2 block text-sm text-ndd-300">End Weight (lbs)</p>
          <NumberPad
            value={endWeightStr}
            onChange={setEndWeightStr}
            placeholder="0"
            formatDisplay
          />
          {lbsPerHeadPreview != null && (
            <p className="mt-2 text-center text-ndd-300">≈ {lbsPerHeadPreview} lbs/head</p>
          )}
        </div>

        {/* Bunk Status */}
        <div>
          <p className="mb-2 block text-sm text-ndd-300">Bunk Status</p>
          <RadioPillGroup
            name="bunk_status"
            options={[
              { value: 'clean', label: 'Clean' },
              { value: 'little_left', label: 'Little Left' },
              { value: 'good_bit_left', label: 'Good Bit' },
              { value: 'all_eaten', label: 'All Eaten' },
              { value: 'sloppy', label: 'Sloppy' },
            ]}
            columns={3}
            register={form.register}
            watch={form.watch}
          />
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="mb-1 block text-sm text-ndd-300">
            Notes
          </label>
          <input
            id="notes"
            type="text"
            placeholder="south side"
            {...form.register('notes')}
            className="w-full rounded-lg bg-ndd-900 px-4 py-3 text-white"
          />
        </div>

        {/* Error message */}
        {mutation.isError && (
          <MutationError error={mutation.error} fallbackMessage="Failed to log feeding" />
        )}

        {/* Submit */}
        <SubmitButton isPending={mutation.isPending} />
      </form>
    </div>
  );
}
